<template>
  <div>
    <v-container fluid :style="{ 'min-height': '800px' }">
      <v-container class="contain-width">

        <v-row>
          <v-col
              md="8"
              offset-md="2"
          >
            <h1 class="mt-6 mb-6">{{ $t('headings.Intro Membership Activ8') }}</h1>
            <v-card
                class="mx-auto form-card"
                outlined
            >
              <v-card-text class="text--primary">

                <p class="body-1">{{ $t("To instantly activate your intro membership, enter the code and your details below") }}.</p>
                <p class="body-1">{{ $t("Already a member?") }} <router-link
                    :to="{ name: 'login' }"
                >{{ $t("links.Login") }}</router-link> {{ $t("to activate more tags.") }}</p>
                <ValidationObserver ref="observer">
                  <v-form v-model="valid" @submit.prevent="handleSubmit">
                    <ValidationProvider vid="qr_code" :name="$t('labels.Activ8 code')" rules="required" v-slot="{ errors }">
                      <v-text-field
                          v-model="code"
                          :error-messages="errors[0]"
                          :label="$t('labels.Activ8 code')"
                          :hint="$t('hints.This is the code on your tag, consisting of letters and numbers')"
                          required
                      ></v-text-field>
                    </ValidationProvider>

                  <ValidationProvider vid="name" :name="$t('labels.Name')" rules="required" v-slot="{ errors }">
                    <v-text-field
                        v-model="name"
                        :error-messages="errors[0]"
                        :label="$t('labels.Name')"
                        required
                    ></v-text-field>
                  </ValidationProvider>

                  <ValidationProvider vid="email" :name="$t('labels.Email')" rules="required|email" v-slot="{ errors }">
                    <v-text-field
                        v-model="email"
                        :error-messages="errors[0]"
                        :label="$t('labels.Email')"
                        required
                    ></v-text-field>
                  </ValidationProvider>

                  <ValidationProvider vid="email_confirmation" :name="$t('labels.Email confirmation')" v-slot="{ errors }" rules="required|confirmed:email">
                    <v-text-field
                        v-model="email_confirmation"
                        :error-messages="errors[0]"
                        :label="$t('labels.Confirm email')"
                        required
                        autocapitalize="none"
                    ></v-text-field>
                  </ValidationProvider>

                  <div class="d-flex align-start">
                    <div style="width: 80px">
                      <v-autocomplete
                          class="mr-2"
                          ref="areaCode"
                          v-model="area"
                          :items="countryCodes"
                          item-text="name"
                          item-value="code"
                      >
                        <template v-slot:selection="data">
                          <span class="select-flag"><gb-flag :code="data.item.lang" size="mini" /></span>
                        </template>
                        <template v-slot:item="{ item }">
                          <span class="select-flag"><gb-flag :code="item.lang" size="mini" /><span class="ml-3">{{ item.name }}</span></span>
                        </template>
                      </v-autocomplete>
                    </div>
                    <div style="width: 100%">
                      <ValidationProvider vid="mobile" :name="$t('labels.Mobile')" :rules="{ required: true, regex: /^[+][?=4|3][4][0-9]+$/ }" v-slot="{ errors }">
                        <v-text-field
                            v-model="mobile"
                            :error-messages="errors[0]"
                            :label="$t('labels.Mobile number (digits only)')"
                            required
                            persistent-hint
                            :placeholder="mobilePlaceholder"
                            :hint="$t('labels.Required for SMS notifications')"
                            @input="updateArea(mobile)"
                        >
                        </v-text-field>
                      </ValidationProvider>
                    </div>
                  </div>

                  <v-divider class="mt-12"></v-divider>

                  <ValidationProvider vid="terms" :name="$t('labels.Terms')" :rules="{ required: { allowFalse: false } }" v-slot="{ errors }">
                    <v-checkbox
                        v-model="terms"
                        :error-messages="errors[0]"
                        required
                    >
                      <template v-slot:label>
                        <div>
                          {{ $t("I have read the") }}
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                              <a
                                  @click.prevent.stop="dialog = true"
                                  v-on="on"
                              >
                                {{ $t('links.terms and conditions') }}
                              </a>
                              <v-dialog
                                  v-model="dialog"
                                  max-width="600"
                              >
                                <v-card>
                                  <v-card-title class="headline">{{ $t('headings.Terms and conditions') }}</v-card-title>

                                  <v-card-text>
                                    <TermsContent/>
                                  </v-card-text>

                                  <v-card-actions>
                                    <v-spacer></v-spacer>

                                    <v-btn
                                        text
                                        @click="rejectTerms"
                                    >
                                      {{ $t('buttons.Disagree') }}
                                    </v-btn>

                                    <v-btn
                                        color="primary"
                                        rounded
                                        elevation=0
                                        @click="acceptTerms"
                                        class="mr-6"
                                    >
                                      {{ $t('buttons.Agree') }}
                                    </v-btn>
                                  </v-card-actions>
                                </v-card>
                              </v-dialog>
                            </template>
                            {{ $t("Opens in popover") }}
                          </v-tooltip>
                        </div>
                      </template>
                    </v-checkbox>
                  </ValidationProvider>

                  <ValidationProvider vid="password" :name="$t('labels.Password')" rules="required|min:8" v-slot="{ errors }">
                    <v-text-field
                        v-model="password"
                        :append-icon="showPassword ? 'visibility' : 'visibility_off'"
                        :type="showPassword ? 'text' : 'password'"
                        :error-messages="errors[0]"
                        :label="$t('labels.Create password')"
                        required
                        autocapitalize="none"
                        @click:append="showPassword = !showPassword"
                    ></v-text-field>
                  </ValidationProvider>

                  <ValidationProvider vid="password_confirmation" :name="$t('labels.Password confirmation')" v-slot="{ errors }" rules="required|min:8|confirmed:password">
                    <v-text-field
                        v-model="password_confirmation"
                        :append-icon="showPassword ? 'visibility' : 'visibility_off'"
                        :type="showPassword ? 'text' : 'password'"
                        :error-messages="errors[0]"
                        :label="$t('labels.Confirm password')"
                        required
                        autocapitalize="none"
                        @click:append="showPassword = !showPassword"
                    ></v-text-field>
                  </ValidationProvider>

                  <vue-programmatic-invisible-google-recaptcha
                      ref="invisibleRecaptcha1"
                      :sitekey="'6Lc1or0UAAAAAHToK0_wzBIEmJSp2BHGNh35DzKL'"
                      :elementId="'invisibleRecaptcha1'"
                      :badgePosition="'left'"
                      :showBadgeMobile="false"
                      :showBadgeDesktop="true"
                      @recaptcha-callback="recaptchaCallback"
                  ></vue-programmatic-invisible-google-recaptcha>

                  <div class="d-flex justify-end">
                    <v-btn v-if="recaptchaFailed" @click="resetRecaptcha" color="error">reCAPTCHA failed: reset</v-btn>

                    <v-btn
                        color="primary"
                        @click="handleSubmit"
                        :loading="loading"
                        :disabled="loading"
                    >
                      {{ $t('buttons.Continue') }} <v-icon>mdi-chevron-right</v-icon>
                      <template v-slot:loader>
                                                              <span class="custom-loader">
                                                                <v-icon light>mdi-loading</v-icon>
                                                              </span>
                      </template>
                    </v-btn>
                  </div>

                  </v-form>
                </ValidationObserver>

              </v-card-text>

            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-container>
    <Footer />
    <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
      {{ snackText }}
      <v-btn text @click="snack = false">{{ $t('buttons.Close') }}</v-btn>
    </v-snackbar>
  </div>

</template>

<script>

import { tagService, userService } from '../services';
import {mapState, mapActions, mapGetters} from 'vuex'
import { store } from '../store';
import { extend } from 'vee-validate';
import { is, required, email, integer, confirmed } from 'vee-validate/dist/rules';
import {i18n} from "../i18n";

extend('confirmed', confirmed);

// Add the required rule
extend('required', required);

// Add the email rule
extend('email', email);

extend('lengthBetween', {
  validate: (value, { min, max }) => {
    const length = value && value.length;

    return length >= min && length <= max;
  },
  params: ['min', 'max'],
  message: i18n.t('The {_field_} length must be between {min} and {max} digits')
});

// Add the min rule
extend('min', {
  validate: (value, { min }) => {
    const length = value && value.length;

    return length >= min;
  },
  params: ['min'],
  message: i18n.t('Please enter a minimum of {min} characters')
});

// Add the min rule
extend('integer', integer);

import Footer from './Footer.vue'
import axios from "axios";
import TermsContent from "./TermsContent";
import {router} from "../helpers";

// Add the required rule
extend('is', is);

export default {
  name: "InstantActivate",
  props: ['qrCode'],
  metaInfo() {
    return {
      title: this.capitalizeFirstLetter(this.brand) + ' - ' + 'Activ8',
      htmlAttrs: {
        lang: i18n.locale
      },
      meta: [
        { name: 'description', content: i18n.t('Enter the code for your activ8 product to activate it; assign the tags to yourself or a dependant') }
      ]
    }
  },
  components: {
    'Footer': Footer,
    'TermsContent': TermsContent
  },
  data: () => ({
    dialog: false,
    code: '',
    tag: {},
    name: '',
    email: '',
    email_confirmation: null,
    mobile: '',
    address1: null,
    address2: null,
    town: null,
    county: null,
    postcode: null,
    country: null,
    password: '',
    password_confirmation: null,
    organisation: null,
    makeTagsPublic: true,
    terms: false,
    submitted: false,
    valid: false,
    showPassword: false,
    recaptchaFailed: false,
    activeOrganisations: [],
    snack: false,
    snackColor: '',
    snackText: '',
    loader: null,
    loading: false,
    area: ''
  }),
  mounted () {



    window.scrollTo(0,0);

    if (this.qrCode) {
      this.code = this.qrCode
    }

    if (this.brand === 'neotag') {
      this.area = '+34'
    }
    else {
      this.area = '+44'
    }

    window.prerenderReady = true;
  },
  computed: {
    ...mapState({
      brand: state => state.brand,
      brandName: state => state.brandName
    }),
    getBackground() {
      if (this.brand === 'neotag') {
        return require('@/assets/neotag-background.jpg')
      }
      else {
        return require('@/assets/hp-banner-1.jpg')
      }
    },
    mobilePlaceholder() {
      if (this.brand === 'neotag') {
        return '+34 000 000 000'
      }
      else {
        return '+44 00000 000 000'
      }
    },
    countryCodes() {
      return [
        {
          name: 'United Kingdom +44',
          lang: 'gb',
          code: '+44'
        },
        {
          name: 'Spain +34',
          lang: 'es',
          code: '+34'
        }
      ]
    }
  },
  watch: {
    area(val) {
      if (val !== '') {
        this.mobile = val
        this.$refs.areaCode.blur();
      }
    }
  },
  methods: {
    ...mapActions('account', ['instantActivate']),
    capitalizeFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    updateArea(mobile) {
      if (mobile.length === 1 && mobile.substring(0, 1) !== '+') {
        this.mobile = '+' + this.mobile
      }

      if (mobile.substring(0, 1) === '+' && mobile.length >= 3) {
        let prefix = mobile.substring(0, 3)

        let area = this.countryCodes.find( record => record.code === prefix)
        if (area) {
          this.area = area.code
        }
      }
    },
    acceptTerms() {
      this.dialog = false
      this.terms = true
    },
    rejectTerms() {
      this.dialog = false
      this.terms = false
    },
    handleSubmit () {

      store.dispatch('alert/clear');

      let observer = this.$refs.observer;

      observer.validate().then(success => {
        if (!success) {
          return;
        }


        this.loader = 'loading'
        this.loading = true

        const {name, email, email_confirmation, mobile, password, password_confirmation, terms} = this;

        if (name && email, email_confirmation && mobile && password && password_confirmation && terms) {

          this.$refs.invisibleRecaptcha1.execute()
        }
      })
    },
    recaptchaCallback (recaptchaToken) {

      let that = this
      console.log(that.card)

      const { code, name, email, email_confirmation, mobile, password, password_confirmation, terms, brand } = this;
      if (code, name && email && email_confirmation && mobile && password && password_confirmation && recaptchaToken && terms && brand) {

        // let organisations = [];
        // if (organisation) {
        //   organisations[0] = organisation
        // }

        let codeString = code;

        let n = code.lastIndexOf('/');
        if (n >= 0) {
          codeString = code.substring(n + 1);
        }

        this.instantActivate({ codeString, name, email, email_confirmation, mobile, password, password_confirmation, recaptchaToken, terms, brand}).then(user => {

          if (!user) {

            // Add the server side errors for each field.
            this.$refs.observer.setErrors(store.getters.validationErrors);

            this.snack = true
            this.snackColor = 'error'
            this.snackText = i18n.t('Please check the form for errors')

            if (store.getters.validationErrors['recaptchaToken']) {
              console.log("recaptchaFailed")
              this.recaptchaFailed = true;
            }

          }

          else {

            window.scrollTo(0,0);

            router.push('/intro-activate-success');

          }

          this.loader = null
          this.loading = false

        })
      }
    },
    resetRecaptcha () {
      this.$refs.invisibleRecaptcha1.reset()
      this.recaptchaFailed = false;
    }
  }
}
</script>

<style scoped>

</style>
